import { useState } from "react";
import { ArrowUpRight, Instagram, Facebook, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { Reveal } from "../components/site/Reveal";
import { sendContact } from "../lib/api";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await sendContact({ ...form, locale: lang });
      toast.success(t.contact.success);
      setForm(initial);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-16 pb-12 md:pt-20 md:pb-16">
        <div className="container-art">
          <Reveal>
            <div className="overline mb-6">{t.contact.overline}</div>
            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tighter leading-[1.02] max-w-3xl text-balance">
              {t.contact.title}
            </h1>
            <p className="mt-8 max-w-xl text-base font-light text-foreground/70 leading-relaxed">
              {t.contact.lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-border">
        <div className="container-art grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Left — info */}
          <Reveal className="md:col-span-5 space-y-12">
            <div>
              <div className="overline mb-5">{t.contact.direct}</div>
              <ul className="space-y-5 font-body text-base font-light">
                <li className="flex items-center gap-3">
                  <Mail size={16} strokeWidth={1.4} className="text-stone" />
                  <a href="mailto:piotr@gonzaga.pl" className="link-underline">
                    piotr@gonzaga.pl
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={16} strokeWidth={1.4} className="text-stone" />
                  <a href="tel:+48507530568" className="link-underline">
                    +48 507 530 568
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} strokeWidth={1.4} className="text-stone mt-1" />
                  <span className="text-foreground/80">
                    Atelier Polska<br />
                    ul. Ratajczaka 44/4<br />
                    61-728 Poznań
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <div className="overline mb-5">{t.contact.socials}</div>
              <ul className="space-y-3 font-body text-base font-light">
                <li>
                  <a
                    href="https://www.instagram.com/gonzaga_myszkowski/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 link-underline"
                  >
                    <Instagram size={14} strokeWidth={1.4} /> Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.facebook.com/piotr.g.myszkowski/?locale=pl_PL"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 link-underline"
                  >
                    <Facebook size={14} strokeWidth={1.4} /> Facebook
                  </a>
                </li>
              </ul>
            </div>
          </Reveal>

          {/* Right — form */}
          <Reveal delay={0.1} className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="flex flex-col gap-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="name" className="overline block mb-3">
                    {t.contact.name}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-name"
                    className="input-line"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="overline block mb-3">
                    {t.contact.email}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-email"
                    className="input-line"
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="overline block mb-3">
                  {t.contact.subject}
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-subject"
                  className="input-line"
                />
              </div>
              <div>
                <label htmlFor="message" className="overline block mb-3">
                  {t.contact.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  className="input-line resize-none"
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-ink/85 transition-colors disabled:opacity-60"
                >
                  {submitting ? t.contact.sending : t.contact.send}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
